import React, { useState, useEffect, useRef } from 'react';
import { Row, Col } from 'react-bootstrap'
import { toast, ToastContainer } from 'react-toastify'
import { useDispatch } from "react-redux";
import useAxiosInstance from '../../Api/AxiosInstance';

const ProductData = () => {
  const [items, setItems] = useState([]);
  const [isLoaded, setIsLoaded] = useState('Loading...');
  const AxiosInstance = useAxiosInstance()
  /* useNavigate/useNavigate import removed with the "View Cart" button - nothing
     in this component navigates any more. PREVIOUS (kept for restore):
     import { useNavigate } from 'react-router-dom';
     const navigate = useNavigate()
  */

  /* ---------------------------------------------------------------------
     ADD-TO-CART STATE (CHANGED)

     The button is now a plain "Add to Cart" that stays that way and can be
     pressed repeatedly - each press adds one more of that medicine. There is
     no "Added" state and no per-card lockout any more.

     `busyIds` / `busyRef` only block the SAME card while its own request is in
     flight, so a double-click cannot post 'add-to-cart' twice for one press.
     A ref is used alongside the state because it flips synchronously: state
     only settles on the next render, and a second click inside that window
     would otherwise get through. Different cards are not blocked by each
     other, so several medicines can be added in quick succession.

     REMOVED on request - the "added" bookkeeping that used to live here drove
     the "Added" label and the one-add-per-card lockout. PREVIOUS (kept for
     restore):
       const [addedIds, setAddedIds] = useState(() => new Set());
       const [busyId, setBusyId] = useState(null);
  --------------------------------------------------------------------- */
  const busyRef = useRef(new Set());
  const [busyIds, setBusyIds] = useState(() => new Set());


  useEffect(() => {
    const fetchMedicine = async () => {
      try {
        const response = await AxiosInstance.post('medicines')
        setItems(response.data.data);
      } catch (error) {

      }
    }
    fetchMedicine()

  }, [])

  /* ---------------------------------------------------------------------
     ADD TO CART (CHANGED)
     PREVIOUS:
       if (items[index]?.addToCartText !== 'View Cart') {
         const response = await AxiosInstance.post("add-to-cart", formData)
         if (response.data.success) {
           if (window.location.pathname === "/cart/") { window.location.reload() }
           document.dispatchEvent(new Event('itemAddedToCart'));
           toast.success('Item added to cart', {...});
           setItems(prevItems => {...updatedItems[index].addToCartText = 'View Cart'; ...});
         }
       }

     Three things changed, all directly asked for:

     1. The `window.location.reload()` on /cart/ is gone. It was a full app
        restart (fresh bundle parse + re-mount) in the middle of a click; the
        'itemAddedToCart' event on the next line already refreshes the cart, so
        the reload was doing the same job the hard way. Note this branch was
        unreachable from the shop page anyway - ProductData renders at /shop/,
        and the only other mount site (Cart.js:145) is commented out - so this
        is dead-code removal rather than a behaviour change.

     2. No "Added" state. Every press adds one more and the label never
        changes, so there is nothing to track per card beyond the in-flight
        guard.

     3. `busyRef` guards the in-flight request against a double click.

     The event still fires AFTER the await, deliberately: ShopPage's listener
     does `fetchCart()`, and the new row must exist server-side before that
     runs. So this path remains 2 round trips - they cannot be merged without a
     backend change that returns the updated cart from 'add-to-cart'. What is
     fixed here is the wasted re-render, the reload, and the duplicate-click
     error.
  --------------------------------------------------------------------- */
  const addToCart = async (id, price, qty, index) => {
    if (busyRef.current.has(id)) return;   // this card is already posting
    busyRef.current.add(id);
    setBusyIds(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    try {
      const formData = new FormData();
      formData.append("medicine_id", id);
      formData.append("price", price);
      formData.append("quantity", qty);
      formData.append("sub_total", price * qty);

      const response = await AxiosInstance.post("add-to-cart", formData)

      if (response.data.success) {
        // Tell the shop page's cart panel to re-read the cart. This is what
        // replaces the old window.location.reload(). Repeated presses simply
        // raise the quantity, so this fires again exactly as before.
        document.dispatchEvent(new Event('itemAddedToCart'));
        toast.success('Item added to cart', {
          position: "top-right",
          autoClose: 1000,
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      busyRef.current.delete(id);
      setBusyIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }

  return (
    <div className='white_box profile-form-block'>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <div className='box-title'>
        <h3>Order Medicines</h3>
      </div>
      <Row className='product-list'>
        {items?.length > 0 ? items?.map((data, idx) => (
          <Col xs={12} md={3} lg={3} className='items' key={idx}>
            <div className='card-prod d-flex'>
              <div className='pro-imgbox'>
                <img src={data.image} alt='' />
              </div>
              <div className='textbox'>
                <h4>{data.name.length > 30 ? `${data.name.substring(0, 30)}...` : data.name}</h4>
                <p>{data.short_description.length > 50 ? `${data.short_description.substring(0, 30)}...` : data.short_description}</p>
              </div>
              <div className='price-box mt-auto'>
                <strong className='prod-price'>₹{data.price}</strong>
              </div>
              <div className='prod-btnbox'>
                {/* Add to Cart must NOT navigate. This was a <Link> whose `to`
                    flipped to '/cart/' as soon as the card was marked added, so
                    every click after the first one navigated to the cart page
                    while the add itself still ran in the background - which is
                    what made "add a product, then add another one" jump to
                    /cart.

                    The root cause of the flip was React reusing these cards: the
                    medicine grid sits in the second position of a Row whose
                    first child (the cart column) appears and disappears as the
                    cart empties and fills. That changes the children list from
                    [false, Col] to [Col, Col], so React matched the existing card
                    subtree to the wrong slot, and the `addedIds` state below came
                    with it - the whole grid already read as "added" before the
                    click. The `key` prop added in ShopPage.js pins the slot so
                    that reuse can no longer happen.

                    With a real button there is no navigation target at all, so
                    neither problem can reappear.

                    CHANGED on request: the label is always "Add to Cart" and the
                    button is never permanently disabled - every press adds one
                    more. It is only disabled for the moment its own request is in
                    flight. PREVIOUS (kept for restore):
                      <button type='button' className='btn addtocard_btn' disabled={busyId === data.id || addedIds.has(data.id)} onClick={() => addToCart(data.id, data.price, 1, idx)}>{addedIds.has(data.id) ? 'Added' : 'Add to Cart'} <i className="las la-shopping-cart"></i></button>

                    ORIGINAL <Link> form:
                      <Link className='btn addtocard_btn' aria-disabled={busyId === data.id} onClick={() => addToCart(data.id, data.price, 1, idx)} to={(addedIds.has(data.id) ? '/cart/' : '')} >{addedIds.has(data.id) ? 'View Cart' : 'Add to Cart'} <i className="las la-shopping-cart"></i></Link>
                */}
                <button type='button' className='btn addtocard_btn' disabled={busyIds.has(data.id)} onClick={() => addToCart(data.id, data.price, 1, idx)}>Add to Cart <i className="las la-shopping-cart"></i></button>
                {/* REMOVED on request: a "View Cart" button used to appear next
                    to this one once the item was added. The cart now lives in the
                    panel on the left of this page, so it was redundant. PREVIOUS
                    (kept for restore):
                    {addedIds.has(data.id) &&
                      <button type='button' className='btn primary small' onClick={() => navigate('/cart/')}>View Cart</button>
                    }
                */}
              </div>
            </div>
          </Col>
        )) :
          <Col xs={12} md={12} lg={12} className='items'>
            <div className='card-prod d-flex'>
              <p>{isLoaded}</p>
            </div>
          </Col>}
      </Row>
    </div>
  )
}

export default ProductData
